import { useSelector } from 'react-redux';
import { styled } from '@mui/system';
import { Typography } from '@components/Typography';
import { RATING_COLOR_RAMP } from '@constants/colors';
import { useTranslation } from '@hooks';
import { formatPercentNumber } from '@helpers/formatNumbers';
import MetricItem from './MetricItem';

const Layout = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
});

const Metrics = styled('div')({
    display: 'grid',
    gridGap: 16,
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    '@media (max-width: 375px)': {
        gridTemplateColumns: '1fr',
    },
});

const ChartContainer = styled('div')({
    width: '100%',
});

const Bars = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
});

const BarRow = styled('div')({
    display: 'grid',
    gridTemplateColumns: '60px 1fr 70px',
    alignItems: 'center',
    gap: 12,
});

const Track = styled('div')({
    height: 8,
    backgroundColor: 'var(--divider)',
    borderRadius: 4,
    overflow: 'hidden',
});

const RATINGS = [5, 4, 3, 2, 1];

const Reviews = () => {
    const { t } = useTranslation();
    const { reviews } = useSelector(state => state.dashboard.dashboard);
    const loaded = useSelector(state => state.dashboard.loaded);

    const data = [
        { id: 0, title: t('Sidebar.reviews'), value: reviews.collected, subvalue: reviews.collectedDelta },
        {
            id: 2,
            title: t('common.rating'),
            value: reviews.averageRating,
            subvalue: reviews.averageRatingDelta,
            withSign: false,
        },
        { id: 3, title: t('HomePage.unreplied'), value: reviews.unrepliedReviews, subvalue: null },
    ];

    return (
        <Layout>
            <Metrics>
                {data.map(item => (
                    <MetricItem
                        key={item.id}
                        value={item.value}
                        subvalue={item.subvalue}
                        title={item.title}
                        withSign={item.withSign}
                        loading={!loaded}
                    />
                ))}
            </Metrics>
            <ChartContainer>
                <Bars>
                    {RATINGS.map(rating => {
                        const set = reviews.ratingDistribution.find(d => d.rating === rating);
                        const count = set?.count ?? 0;
                        const percentage = set?.percentage ?? 0;
                        const color = RATING_COLOR_RAMP[rating - 1];
                        return (
                            <BarRow key={rating}>
                                <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                                    {t(`AnalyticsReviews.stars${rating}`)}
                                </Typography>
                                <Track>
                                    <div
                                        style={{
                                            height: '100%',
                                            width: `${Math.max(percentage, 0)}%`,
                                            backgroundColor: color,
                                            borderRadius: 4,
                                            transition: 'width 200ms ease',
                                        }}
                                    />
                                </Track>
                                <Typography
                                    variant="caption"
                                    sx={{ color: 'var(--text-secondary)', textAlign: 'right' }}
                                >
                                    {count} ({formatPercentNumber(percentage)})
                                </Typography>
                            </BarRow>
                        );
                    })}
                </Bars>
            </ChartContainer>
        </Layout>
    );
};

export default Reviews;
